import { useQuery, UseQueryResult, useMutation, UseMutationResult } from 'react-query';
import StreamKeyListItem from '../entities/stream-key-list-item';
import Api from './api'; // Lambda backend for stream keys CRUD
import ApiV2 from './api-v2'; // Next.js backend for managers and events
import { ListResponse } from '../entities/list-response';

export const GET_STREAM_KEY_CACHE_KEY = 'list-stream-keys';
export const GET_STREAM_KEY_MANAGERS_CACHE_KEY = 'stream-key-managers';

export interface StreamKeyManager {
  id: string;
  streamKeyId: string;
  userId: string;
  isActive: boolean;
  email?: string;
  name?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateStreamKeyRequest {
  eventId?: string;
  name?: string;
}

export interface AssignManagerRequest {
  userId: string;
  streamKey: string;
  eventId?: string;
}

interface StreamKeyWithEventResponse {
  id: string;
  streamKey: string;
  ingestServer?: string;
  playbackUrl?: string;
  channelName?: string;
  orgId: string;
  eventId?: string;
  eventName?: string;
  type?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface StreamKeyApiResponse {
  success: boolean;
  data?: StreamKeyWithEventResponse[];
  error?: string;
}

/**
 * Gets all stream keys with merged data from both backends
 *
 * @reason Fetches legacy fields from Lambda and new fields (eventId) from eos-app.
 * For managers, only returns their assigned stream keys from eos-app.
 */
export function useGetStreamKeys(role?: string): UseQueryResult<ListResponse<StreamKeyListItem>> {
  return useQuery([GET_STREAM_KEY_CACHE_KEY, role], async () => {
    const isManager = role === 'manager';

    // For managers: only fetch from eos-app (returns only their assigned stream keys)
    if (isManager) {
      try {
        const eosResponse = await ApiV2.get<StreamKeyApiResponse>('/api/stream-keys');
        const eosItems = eosResponse?.data ?? [];

        const items: StreamKeyListItem[] = eosItems.map((item) => ({
          id: item.id,
          streamKey: item.streamKey,
          ingestServer: item.ingestServer ?? '',
          playbackUrl: item.playbackUrl ?? '',
          name: item.channelName ?? item.streamKey,
          type: item.type ?? 'RTMP',
          isBlackListed: false,
          eventId: item.eventId,
          eventName: item.eventName,
          orgId: item.orgId,
        }));

        return { items };
      } catch (error) {
        console.warn('Failed to fetch assigned stream keys from eos-app:', error);
        return { items: [] };
      }
    }

    // For admins/owners: merge legacy Lambda data with eos-app data
    const legacyData = await Api.get<ListResponse<StreamKeyListItem>>('/stream-keys');

    // Normalize legacy items and index by stream key for merging
    const streamKeyMap = new Map<string, StreamKeyListItem>();
    legacyData.items
      .filter((item): item is StreamKeyListItem => item != null && Boolean(item.streamKey))
      .forEach((item) => {
        streamKeyMap.set(item.streamKey, { ...item });
      });

    // Fetch event-assigned keys from eos-app (api-v2.ts)
    try {
      const eosResponse = await ApiV2.get<StreamKeyApiResponse>('/api/stream-keys');
      const eosItems = eosResponse?.data ?? [];

      eosItems.forEach((item) => {
        const existing = streamKeyMap.get(item.streamKey);

        const merged: StreamKeyListItem = {
          id: existing?.id ?? item.id,
          streamKey: item.streamKey,
          ingestServer: existing?.ingestServer ?? item.ingestServer ?? '',
          playbackUrl: existing?.playbackUrl ?? item.playbackUrl ?? '',
          name: existing?.name ?? item.channelName ?? item.streamKey,
          type: existing?.type ?? item.type ?? 'RTMP',
          isBlackListed: existing?.isBlackListed ?? false,
          eventId: item.eventId ?? existing?.eventId,
          eventName: item.eventName ?? existing?.eventName,
          orgId: item.orgId ?? existing?.orgId,
        };

        streamKeyMap.set(item.streamKey, merged);
      });
    } catch (error) {
      console.warn('Failed to fetch stream keys from eos-app:', error);
    }

    legacyData.items = Array.from(streamKeyMap.values());
    return legacyData;
  });
}

/**
 * Updates a stream key (e.g., assign to event)
 *
 * @reason Allows updating stream key properties like eventId
 * @note Uses api-v2.ts (eos-app) for new columns only
 */
export function useUpdateStreamKey(
  streamKeyValue: string,
): UseMutationResult<void, unknown, UpdateStreamKeyRequest, unknown> {
  return useMutation(async (updates: UpdateStreamKeyRequest) => {
    // Use ApiV2 (eos-app Next.js backend) for new columns (eventId)
    // Pass the actual stream key value (sk_...) not the DynamoDB id
    await ApiV2.patch(`/api/stream-keys/${streamKeyValue}`, updates);
  });
}

/**
 * Gets managers assigned to a stream key
 *
 * @reason Shows who has access to manage this stream key
 */
export function useGetStreamKeyManagers(streamKeyId?: string): UseQueryResult<StreamKeyManager[]> {
  return useQuery(
    [GET_STREAM_KEY_MANAGERS_CACHE_KEY, streamKeyId],
    async () => {
      if (!streamKeyId) return [];
      // Use ApiV2 (eos-app Next.js backend) for manager operations
      const response = await ApiV2.get<{ success: boolean; data: StreamKeyManager[] }>(
        `/api/stream-keys/${streamKeyId}/managers`,
      );
      return response.data;
    },
    {
      enabled: !!streamKeyId,
    },
  );
}

/**
 * Assigns a manager to a stream key
 *
 * @reason Allows admins to grant stream key management access
 */
export function useAssignManagerToStreamKey(
  streamKeyId: string,
): UseMutationResult<StreamKeyManager, unknown, Omit<AssignManagerRequest, 'streamKey'>, unknown> {
  return useMutation(async (data: Omit<AssignManagerRequest, 'streamKey'>) => {
    // Use ApiV2 (eos-app Next.js backend) for manager operations
    const response = await ApiV2.post<{ success: boolean; data: StreamKeyManager }>(
      `/api/stream-keys/${streamKeyId}/managers`,
      {
        ...data,
        streamKey: streamKeyId, // Add streamKey to body as required by schema
      },
    );
    return response.data;
  });
}

/**
 * Removes a manager from a stream key
 *
 * @reason Allows admins to revoke stream key management access
 */
export function useRemoveManagerFromStreamKey(
  streamKeyId: string,
): UseMutationResult<void, unknown, string, unknown> {
  return useMutation(async (userId: string) => {
    // Use ApiV2 (eos-app Next.js backend) for manager operations
    await ApiV2.delete(`/api/stream-keys/${streamKeyId}/managers/${userId}`);
  });
}
